import { Button, Checkbox } from '@porterin/component-library/src/index';
import { isMinWidth } from '@porterin/component-library/src/utils/BrowserUtils';
import Image from 'next/image';
import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';

interface ConditionVM {
  id: number;
  text: string | JSX.Element;
  isRequired: boolean;
}

export interface ConsentState {
  id: number;
  isConsentGiven: boolean;
}

export interface ConsentFormProps {
  id: string;
  onClose: () => void;
  onConfirm: (consents: ConsentState[]) => void;
  conditions: ConditionVM[];
  isAllSelectedOnOpen?: boolean;
  cmsData: {
    title: string;
    description: string;
    agreeAllText: string;
    closeIcon: { src: string; alt: string };
    confirmText: string;
  };
}

export const isEveryConsentGiven = (consents: ConsentState[]) => {
  return consents.every(consent => consent.isConsentGiven);
};

export const ConsentForm: React.FC<ConsentFormProps> = ({
  id,
  onClose,
  onConfirm,
  conditions,
  isAllSelectedOnOpen: isAllSelectedOnLoad = false,
  cmsData,
}) => {
  const [consents, setConsents] = useState<ConsentState[]>(
    conditions.map(condition => ({
      id: condition.id,
      isConsentGiven: isAllSelectedOnLoad,
    })),
  );

  const isDesktop = useMemo(() => isMinWidth('sm'), []);

  return (
    <div className="rounded-t-[0.8rem] sm:rounded-[1.6rem] max-sm:rounded-b-none bg-[#FCFCFC] md:w-[72.4rem]">
      <div className="shadow-[0rem_0.2rem_0.8rem_0rem_#0000000D] relative p-[1.6rem] sm:p-[2.6rem_3.2rem]">
        <h3 className="font-bold text-[1.4rem] sm:text-[1.6rem] leading-[1.4rem] sm:leading-[1.6rem] m-0 p-0 text-[#2D2F34]">
          {cmsData.title}
        </h3>
        <Image
          className="absolute top-[1.4rem] right-[1.6rem]"
          src={cmsData.closeIcon.src}
          alt={cmsData.closeIcon.alt}
          width={isDesktop ? 24 : 20}
          height={isDesktop ? 24 : 20}
          onClick={onClose}
        />
      </div>
      <div className="p-[1.6rem_1.6rem_4.8rem] sm:p-[2.4rem_3.2rem_4.8rem]">
        <div className="font-medium text-[1.2rem] sm:text-[1.4rem] leading-[1.8rem] sm:leading-[2rem] mb-[2.4rem] text-[#2D2F34]">
          {cmsData.description}
        </div>
        <div>
          <Checkbox
            identifiers={{
              id: `checkbox-terms-all`,
            }}
            className="mb-[1.6rem]"
            checkboxOptions={{
              value: 'agree_all',
              label: (
                <span className="font-semibold text-[1.2rem] sm:text-[1.4rem] leading-[2rem] text-[#2D2F34]">
                  {cmsData.agreeAllText}
                </span>
              ),
              checked: consents.every(consent => consent.isConsentGiven),
            }}
            interactions={{
              onChange: isChecked => {
                setConsents(prevConsents =>
                  prevConsents.map(consent => ({
                    ...consent,
                    isConsentGiven: isChecked,
                  })),
                );
              },
            }}
          />
          <div className="pl-[2.4rem] flex flex-col gap-[1.6rem]">
            {conditions.map(condition => (
              <Checkbox
                key={condition.id}
                identifiers={{
                  id: `checkbox-terms-${condition.id}`,
                }}
                className="flex items-start p-[0.2rem]"
                interactions={{
                  onChange: isChecked => {
                    setConsents(prevConsents =>
                      prevConsents.map(consent => ({
                        ...consent,
                        isConsentGiven:
                          condition.id === consent.id
                            ? isChecked
                            : consent.isConsentGiven,
                      })),
                    );
                  },
                }}
                checkboxOptions={{
                  value: condition.id.toString(),
                  label: (
                    <span>
                      {typeof condition.text === 'string' ? (
                        <ReactMarkdown>{condition.text}</ReactMarkdown>
                      ) : (
                        condition.text
                      )}
                    </span>
                  ),
                  checked: consents.find(consent => consent.id === condition.id)
                    ?.isConsentGiven,
                  labelClass:
                    'text-[1.2rem] sm:text-[1.4rem] font-normal leading-[2rem] -mt-[0.3rem] [&_a]:underline [&_a]:text-[#0A57FF]',
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="text-center py-[1.6rem] shadow-[0rem_-0.2rem_0.8rem_0rem_#00000014]">
        <Button
          className={`w-[calc(100%_-_calc(2_*_2.4rem))] p-[1.6rem] ${isEveryConsentGiven(consents) ? 'bg-[#0A57FF]' : 'bg-[#ECEDEE]'} rounded-[0.4rem] text-[#FCFCFC] text-[1.4rem] leading-[2rem] font-semibold`}
          onClick={() => onConfirm(consents)}
          identifiers={{
            id: `confirm-${id}`,
          }}
          disabled={!isEveryConsentGiven(consents)}
        >
          {cmsData.confirmText}
        </Button>
      </div>
    </div>
  );
};

export default ConsentForm;
